import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, ShoppingCart, Plus, Minus, Check, X, AlertTriangle, Banknote } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientStorePage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [ordering, setOrdering] = useState(null);
  const [ordered, setOrdered] = useState({});
  const [confirmOrder, setConfirmOrder] = useState(null);

  useEffect(() => {
    axios.get(`${API}/store/products`).then(res => {
      setProducts(res.data);
      const q = {};
      res.data.forEach(p => { q[p.id] = 1; });
      setQuantities(q);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const updateQty = (id, delta) => {
    setQuantities(prev => ({ ...prev, [id]: Math.max(1, (prev[id] || 1) + delta) }));
  };

  const handleOrder = async (product) => {
    setOrdering(product.id);
    try {
      await axios.post(`${API}/store/orders`, {
        product_id: product.id,
        quantity: quantities[product.id] || 1,
      });
      setOrdered(prev => ({ ...prev, [product.id]: true }));
      setConfirmOrder(null);
      toast.success(`Orden de ${product.name} creada exitosamente`);
      setTimeout(() => setOrdered(prev => ({ ...prev, [product.id]: false })), 3000);
    } catch {
      toast.error('Error al crear la orden');
    } finally {
      setOrdering(null);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-5 animate-fade-in pb-8" data-testid="client-store-page">
      <button onClick={() => navigate('/client/supplements')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-supplements">
        <ArrowLeft size={16} /> Suplementos
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Tienda
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Selecciona un producto y realiza tu orden</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin" />
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-store-products">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-indigo-300" />
          </div>
          <p className="font-bold text-gray-700" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin productos disponibles</p>
          <p className="text-sm text-gray-400 mt-1">El catalogo estara disponible pronto</p>
        </div>
      ) : (
        <div className="space-y-4">
          {products.map((product) => {
            const qty = quantities[product.id] || 1;
            const total = product.sale_price * qty;
            const isOrdered = ordered[product.id];
            const isOrdering = ordering === product.id;

            return (
              <div
                key={product.id}
                className="bg-white rounded-3xl border border-gray-100/50 overflow-hidden shadow-sm"
                data-testid={`store-product-${product.id}`}
              >
                <div className="h-1.5 bg-gradient-to-r from-indigo-400 via-violet-500 to-purple-500" />
                <div className="p-5">
                  {/* Product info */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {product.name}
                      </p>
                      <span className="inline-block text-[10px] font-bold uppercase tracking-wide mt-1 px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                        {product.type === 'semanal' ? '7 dias' : product.type === 'mensual' ? '28 dias' : product.type}
                      </span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        RD${product.sale_price.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-gray-400 font-medium">por unidad</p>
                    </div>
                  </div>

                  {/* Quantity selector */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-3 mb-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Cantidad</span>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQty(product.id, -1)}
                        className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:border-indigo-300 hover:text-indigo-500 transition-colors"
                        data-testid={`qty-minus-${product.id}`}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="text-lg font-bold text-gray-900 w-8 text-center" data-testid={`qty-value-${product.id}`}>
                        {qty}
                      </span>
                      <button
                        onClick={() => updateQty(product.id, 1)}
                        className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:border-indigo-300 hover:text-indigo-500 transition-colors"
                        data-testid={`qty-plus-${product.id}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Total + Order button */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Total</p>
                      <p className="text-xl font-black text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        RD${total.toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setConfirmOrder(product)}
                      disabled={isOrdering || isOrdered}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-300 shadow-md ${
                        isOrdered
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white hover:shadow-lg hover:-translate-y-0.5'
                      } disabled:opacity-70`}
                      data-testid={`order-btn-${product.id}`}
                    >
                      {isOrdered ? (
                        <><Check size={16} /> Ordenado</>
                      ) : isOrdering ? (
                        'Procesando...'
                      ) : (
                        <><ShoppingCart size={16} /> Ordenar</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Confirm Order Modal */}
      {confirmOrder && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmOrder(null)}>
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="confirm-order-modal">
            <div className="h-1.5 bg-gradient-to-r from-indigo-400 via-violet-500 to-purple-500" />
            <div className="p-6">
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={28} className="text-amber-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 text-center mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Confirmar Orden
              </h3>
              <p className="text-sm text-gray-500 text-center mb-5">
                Estas a punto de ordenar:
              </p>

              <div className="bg-gray-50 rounded-2xl p-4 mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Producto</span>
                  <span className="font-bold text-gray-900">{confirmOrder.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cantidad</span>
                  <span className="font-bold text-gray-900">{quantities[confirmOrder.id] || 1}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between text-sm">
                  <span className="text-gray-500">Total</span>
                  <span className="font-black text-lg text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    RD${(confirmOrder.sale_price * (quantities[confirmOrder.id] || 1)).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200/60 rounded-xl p-3 mb-5">
                <Banknote size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs font-medium text-amber-800 leading-relaxed">
                  Este pedido debe ser pagado en efectivo o transferencia al momento de la entrega.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmOrder(null)}
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                  data-testid="cancel-order-btn"
                >
                  No, cancelar
                </button>
                <button
                  onClick={() => handleOrder(confirmOrder)}
                  disabled={ordering === confirmOrder.id}
                  className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-indigo-500 to-violet-500 hover:shadow-lg transition-all disabled:opacity-70"
                  data-testid="confirm-order-btn"
                >
                  {ordering === confirmOrder.id ? 'Procesando...' : 'Si, confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientStorePage;
